import cs from "../locales/cs.json";
import en from "../locales/en.json";
import type { Locale } from "../shared/dto";
import { applyGender, type ClientGender } from "../shared/gender";
import { preferredLocale } from "../shared/locale-preference";

const dictionaries = { cs, en } as const;

/**
 * F116: the language the reader's own browser asks for. The login screen
 * renders BEFORE any session exists, so this is the only thing that can speak
 * for an American client - `setLocale` runs on the `/api/auth/me` answer, which
 * a guest never gets. A hardcoded "cs" here meant every en-US browser with an
 * empty store read the first screen of the product in Czech, with the language
 * switch locked away inside the app.
 *
 * The choice itself lives in `shared/locale-preference` because the Worker's
 * /go/ landing pages have to answer the same question the same way.
 */
export function browserLocale(): Locale {
  const stored = localStorage.getItem("launchpad-locale");
  if (stored === "cs" || stored === "en") return stored;
  const navigatorTags = typeof navigator === "undefined"
    ? []
    : navigator.languages ?? (navigator.language ? [navigator.language] : []);
  return preferredLocale(navigatorTags);
}

/**
 * F64: Czech copy carries both genders as {{g:masculine|feminine}} and this is
 * the single place the browser resolves them. Every surface already receives
 * its `t` from one closure, so passing the client's gender in there conjugates
 * the whole client side at once; an unknown gender renders "spustil/a".
 */
export function translate(locale: Locale, key: string, gender: ClientGender = null): string {
  const value = key.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[part];
  }, dictionaries[locale]);
  if (typeof value !== "string") throw new Error(`Missing locale key: ${locale}.${key}`);
  return applyGender(value, gender);
}

export function rememberLocale(locale: Locale): void {
  localStorage.setItem("launchpad-locale", locale);
}

// Czech declines with the count (1 připomínku / 2 připomínky / 5 připomínek);
// a key that ends in a plural group ({ one, few, other }) picks the right
// form via CLDR rules and fills {count}. English uses one/other.
const pluralRules: Partial<Record<Locale, Intl.PluralRules>> = {};

export function translateCount(locale: Locale, key: string, count: number): string {
  return translatePluralTemplate(locale, key, count).replace("{count}", String(count));
}

export function translateCountLabel(locale: Locale, key: string, count: number): string {
  return translatePluralTemplate(locale, key, count).replace("{count}", String(count));
}

function translatePluralTemplate(locale: Locale, key: string, count: number): string {
  const rules = pluralRules[locale] ?? (pluralRules[locale] = new Intl.PluralRules(locale === "cs" ? "cs" : "en"));
  let template: string;
  try {
    template = translate(locale, `${key}.${rules.select(count)}`);
  } catch {
    template = translate(locale, `${key}.other`);
  }
  return template;
}
